import React, { FC, useEffect, useState } from "react";
import Sheet from "@mui/joy/Sheet";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import CssBaseline from "@mui/joy/CssBaseline";
import LoadingIcon from "../../../common/LoadingIcon";
import ToastMessage from "../../../common/ToastMessage";
import useScreenWidth from "../../../store/useScreenWidth";
import CreateRoundedIcon from "@mui/icons-material/CreateRounded";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import useAddIncident from "../../../store/ReactQueries/incidents/useAddIncident";
import useIncidentById from "../../../store/ReactQueries/incidents/useIncidentById";
import useUpdateIncident from "../../../store/ReactQueries/incidents/useUpdateIncident";
import { EndPoints } from "../../../utils/apiEndpoints";
import { useNavigate, useParams } from "react-router-dom";
import { IncidentInterface } from "../../../models/Incident";
import { FieldErrors } from "../../../domain/helpers/Errors";
import { validateSchema } from "../../../common/ValidateSchema";
import { useDarkModeStore } from "../../../store/useDarkModeStore";
import { darkModeBackgroudInputs } from "../../../utils/constants";
import { useLanguageStore } from "../../../store/useLanguageStore";
import { incidentSchema } from "../../../domain/ValidationSchemas/Ticket/incident";
import CustomizedInputsStyled, { IncidentTypeInputs } from "./CustomizedInputsStyled";

interface AddIncidentProps {
  view?: boolean;
}

export interface GenerateInput<T> {
  id: number;
  name: string;
  label: string;
  type: IncidentTypeInputs;
  data?: T;
}
export const AddIncident: FC<AddIncidentProps> = ({ view = false }) => {
  const navigate = useNavigate();
  const addNewIncident = useAddIncident(0, 0);
  const isSmallScreen = useScreenWidth();

  const { darkMode } = useDarkModeStore();
  const { language } = useLanguageStore();
  const { id: incidentId } = useParams<{ id: string }>();
  const { data: incidentById, error: incidentByIdError, isLoading } = useIncidentById(incidentId || "");

  const updateIncident = useUpdateIncident(0, 0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const initialState: IncidentInterface = {
    id: "",
    title: "",
    stepsTaken: "",
    dateTime: undefined,
  };
  const [form, setForm] = useState<IncidentInterface>(initialState);
  const [disableSaveBtn, setDisableBtnSave] = useState<boolean>(false);

  useEffect(() => {
    if (incidentByIdError) {
      ToastMessage.Failed(`Error: ${incidentByIdError.message}`);
      navigate(-1);
    }
  }, [incidentByIdError]);

  useEffect(() => {
    if (incidentId && incidentById) {
      let result = incidentById as IncidentInterface;
      setForm(result);
    } else setForm(initialState);
  }, [incidentId, incidentById]);

  const generateInputs: GenerateInput<any>[] = [
    { id: 1, name: "dateTime", label: "Time", type: "date" },
    { id: 2, name: "title", label: "Incident", type: "multitext" },
    { id: 3, name: "stepsTaken", label: "TakenSteps", type: "multitext" },
  ];

  const handleSuccess = (resolve: (value: void | PromiseLike<void>) => void) => {
    ToastMessage.Success();
    navigate(EndPoints.INCIDENTS);
    resolve();
  };

  const handleError = (error: unknown, reject: (reason?: any) => void) => {
    if (error instanceof Error) ToastMessage.Failed(error.message);
    else ToastMessage.Failed();

    reject(error);
  };

  const handleSave = async () => {
    const isValid = validateSchema(incidentSchema, form);
    setErrors(isValid.errors);
    if (!isValid.result) return;

    setDisableBtnSave(true);

    try {
      await new Promise<void>((resolve, reject) => {
        let upForm: IncidentInterface = {
          id: form.id,
          title: form.title,
          dateTime: form.dateTime,
          stepsTaken: form.stepsTaken,
        };
        if (upForm.id === "") {
          const { id, ...formData } = upForm;
          addNewIncident.mutate(formData, {
            onSuccess: () => handleSuccess(resolve),
            onError: (error) => handleError(error, reject),
          });
        } else {
          updateIncident.mutate(upForm, {
            onSuccess: () => handleSuccess(resolve),
            onError: (error: unknown) => handleError(error, reject),
          });
        }
      });
    } catch (ex: unknown) {
      if (ex instanceof Error) {
        console.log(ex.message);
      } else {
        console.log("An unknown error occurred");
      }
    }
    setDisableBtnSave(false);
  };

  return (
    <div className="w-100 d-flex justify-content-center ">
      <CssBaseline />
      {isLoading ? (
        <LoadingIcon width={50} />
      ) : (
        <Sheet
          sx={{
            width: isSmallScreen ? "90%" : "60%",
            my: 1,
            py: 4,
            px: 4,
            display: "flex",
            flexDirection: "column",
            gap: 0,
            borderRadius: "md",
            boxShadow: "8px 8px 30px rgba(0, 0, 0, 0.2)",
            background: darkMode ? "var(--dark-mode-bg)" : "white",
          }}
        >
          <h5 className="mb-0 " style={{ color: darkMode ? "white" : "rgba(0, 0, 0, 0.6)" }}>
            <BuildOutlinedIcon className="me-2 pb-1" />
            {language["Incident"]} - {view ? language["View"] : incidentId === "" ? language["Add"] : language["Edit"]}
            {view && (
              <IconButton
                className={`ms-2 ${darkMode && "text-white"}`}
                title={language["Edit"]}
                onClick={() => navigate(`${EndPoints.INCIDENTS}/edit/${form.id}`)}
              >
                <CreateRoundedIcon sx={{ width: 18, height: 18 }} />
              </IconButton>
            )}
          </h5>
          <hr className={darkMode ? "text-white" : ""} />
          <form
            className={`${isSmallScreen ? "d-flex flex-column" : "module-form-grid"} gap-2`}
            style={{ maxHeight: "calc(100vh - 300px)", overflowY: "auto" }}
          >
            {generateInputs?.map(({ id, name, label, type }) => (
              <CustomizedInputsStyled
                key={id}
                name={name}
                label={label}
                type={type}
                form={form}
                setForm={setForm}
                errors={errors}
                view={view}
              />
            ))}
          </form>
          <hr className={darkMode ? "text-white" : ""} />
          <div className="d-flex flex-row justify-content-end gap-2" style={{ marginTop: 10 }}>
            <Button
              sx={{
                fontSize: "16px",
                fontWeight: 700,
                background: darkMode ? darkModeBackgroudInputs : "white",
                border: `1px solid var(--btn-color)`,
                color: darkMode ? "white" : "var(--btn-color)",
                "&:hover": {
                  background: darkMode ? darkModeBackgroudInputs : "white",
                  opacity: 0.7,
                },
              }}
              onClick={() => navigate(EndPoints.INCIDENTS)}
            >
              {language["Cancel"]}
            </Button>
            {!view && (
              <Button
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  background: "var(--btn-color)",
                  color: "white",
                  width: 85,
                  "&:hover": {
                    background: "var(--btn-color)",
                    opacity: 0.7,
                  },
                }}
                onClick={handleSave}
              >
                {disableSaveBtn ? <LoadingIcon width={22} /> : language["Save"]}
              </Button>
            )}
          </div>
        </Sheet>
      )}
    </div>
  );
};
export default AddIncident;
